import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Table, Button, Form, Pagination, InputGroup, Modal } from 'react-bootstrap';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import axios from 'axios';
import { BsPencilSquare, BsTrash, BsSearch, BsSortDown, BsSortUp, BsPlus } from 'react-icons/bs';
import { API_ENDPOINTS } from '../config';

const LabServices = () => {
  const [labServices, setLabServices] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [labServicesPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('labServiceId');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newLabService, setNewLabService] = useState({
    providerId: '',
    labServiceName: '',
    labServiceAmount: '',
    labServiceDescription: ''
  });
  const [providerServices, setProviderServices] = useState([]);
  const [currentEditLabService, setCurrentEditLabService] = useState(null);

  useEffect(() => {
    fetchLabServices();
  }, []);

  const fetchLabServices = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.getAllLabServices, {
        headers: {
          'token': localStorage.getItem('token'),
        }
      });
      setLabServices(response.data.data);
    } catch (error) {
      console.error('Error fetching lab services:', error);
    }
  };

  const fetchProviderServices = async (providerId) => {
    try {
      const response = await axios.get(API_ENDPOINTS.getProviderServices, {
        headers: {
          'token': localStorage.getItem('token'),
          'providerid': providerId,
        },
      });
      if (response.data.status) {
        setProviderServices(response.data.providerService);
      } else {
        setProviderServices([]);
      }
    } catch (error) {
      console.error('Error fetching provider services:', error);
      alert('Error fetching provider services. Please check the provider ID.');
    }
  };

  const sortLabServices = (field) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedAndFilteredLabServices = labServices
    .filter(service =>
      service.labServiceName.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (a[sortField] < b[sortField]) return sortDirection === 'asc' ? -1 : 1;
      if (a[sortField] > b[sortField]) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const indexOfLastLabService = currentPage * labServicesPerPage;
  const indexOfFirstLabService = indexOfLastLabService - labServicesPerPage;
  const currentLabServices = sortedAndFilteredLabServices.slice(indexOfFirstLabService, indexOfLastLabService);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleEdit = (service) => {
    setCurrentEditLabService(service);
    setShowEditModal(true);
  };

  const handleDelete = async (labServiceId) => {
    try {
      await axios.delete(API_ENDPOINTS.deleteLabService, {
        headers: {
          'token': localStorage.getItem('token'),
          'labserviceid': labServiceId
        }
      });
      setLabServices(labServices.filter(service => service.labServiceId !== labServiceId));
    } catch (error) {
      console.error('Error deleting lab service:', error);
    }
  };

  const handleAddLabService = async () => {
    try {
      const response = await axios.post(API_ENDPOINTS.addLabService, newLabService, {
        headers: {
          'token': localStorage.getItem('token'),
        },
      });
      
      if (response.status === 201) {
        console.log('Lab service added successfully:', response.data);
        setShowAddModal(false);
        fetchLabServices();
      } else {
        console.error('Unexpected response status:', response.status);
      }
    } catch (error) {
      if (error.response) {
        console.error('Error adding lab service:', error.response.data);
        alert(`Error adding lab service: ${error.response.data.message || 'Unknown error'}`);
      } else if (error.request) {
        console.error('No response received from the server:', error.request);
        alert('No response from the server. Please try again later.');
      } else {
        console.error('Error setting up the request:', error.message);
        alert('Error occurred while adding lab service. Please check the console for details.');
      }
    }
  };

  const handleEditLabService = async () => {
    try {
      await axios.patch(API_ENDPOINTS.updateLabService, currentEditLabService, {
        headers: {
          'token': localStorage.getItem('token'),
          'labserviceid': currentEditLabService.labServiceId
        }
      });
      setShowEditModal(false);
      fetchLabServices();
    } catch (error) {
      console.error('Error editing lab service:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (showAddModal) {
      setNewLabService({ ...newLabService, [name]: value });
      if (name === 'providerId') {
        fetchProviderServices(value); // Fetch provider services when provider ID is entered
      }
    } else if (showEditModal) {
      setCurrentEditLabService({ ...currentEditLabService, [name]: value });
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'row', height: '100vh' }}>
      <Sidebar style={{ position: 'fixed', top: 0, bottom: 0, left: 0, width: '250px', zIndex: 1000 }} />
      <div style={{ width: 'calc(100%)', height: 'calc(100vh)', overflowY: 'auto' }}>
        <Header style={{ position: 'fixed', top: 0, left: '250px', width: 'calc(100% - 250px)', zIndex: 1000 }} />
        <Container fluid>
          <Row className="mb-3">
            <Col>
              <div className="d-flex justify-content-between align-items-center">
                <h2>Lab Services</h2>
                <Button variant="primary" onClick={() => setShowAddModal(true)}>
                  <BsPlus /> Add Service
                </Button>
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <InputGroup className="mb-3">
                <InputGroup.Text><BsSearch /></InputGroup.Text>
                <Form.Control
                  placeholder="Search services..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
              <div style={{ overflowX: 'auto' }}>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th onClick={() => sortLabServices('labServiceId')}>
                        Service ID {sortField === 'labServiceId' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                      </th>
                      <th onClick={() => sortLabServices('laboratoryName')}>
                        Laboratory Name {sortField === 'laboratoryName' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                      </th>
                      <th onClick={() => sortLabServices('labServiceName')}>
                        Service Name {sortField === 'labServiceName' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                      </th>
                      <th>Description</th>
                      <th onClick={() => sortLabServices('labServiceAmount')}>
                        Amount {sortField === 'labServiceAmount' && (sortDirection === 'asc' ? <BsSortUp /> : <BsSortDown />)}
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentLabServices.map((service) => (
                      <tr key={service.labServiceId}>
                        <td>{service.labServiceId}</td>
                        <td>{service.laboratoryName}</td>
                        <td>{service.labServiceName}</td>
                        <td style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {service.labServiceDescription}
                        </td>
                        <td>{service.labServiceAmount}</td>
                        <td>
                          <Button variant="warning" onClick={() => handleEdit(service)}>
                            <BsPencilSquare />
                          </Button>
                          <Button variant="danger" onClick={() => handleDelete(service.labServiceId)}>
                            <BsTrash />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <Pagination>
                {[...Array(Math.ceil(sortedAndFilteredLabServices.length / labServicesPerPage))].map((_, index) => (
                  <Pagination.Item key={index + 1} active={index + 1 === currentPage} onClick={() => paginate(index + 1)}>
                    {index + 1}
                  </Pagination.Item>
                ))}
              </Pagination>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Add Service Modal */}
      <Modal show={showAddModal} onHide={() => setShowAddModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Add Lab Service</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="providerId">
              <Form.Label>Provider ID</Form.Label>
              <Form.Control
                type="text"
                name="providerId"
                value={newLabService.providerId}
                onChange={handleInputChange}
                placeholder="Enter provider ID"
              />
            </Form.Group>
            <Form.Group controlId="labServiceName">
              <Form.Label>Service Name</Form.Label>
              <Form.Control
                as="select"
                name="labServiceName"
                value={newLabService.labServiceName}
                onChange={handleInputChange}
              >
                <option value="">Select a service</option>
                {providerServices.map((service, index) => (
                  <option key={index} value={service}>
                    {service}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            <Form.Group controlId="labServiceAmount">
              <Form.Label>Service Amount</Form.Label>
              <Form.Control
                type="text"
                name="labServiceAmount"
                value={newLabService.labServiceAmount}
                onChange={handleInputChange}
                placeholder="Enter service amount"
              />
            </Form.Group>
            <Form.Group controlId="labServiceDescription">
              <Form.Label>Service Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="labServiceDescription"
                value={newLabService.labServiceDescription}
                onChange={handleInputChange}
                placeholder="Enter service description"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleAddLabService}>
            Add Service
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Service Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Lab Service</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="labServiceName">
              <Form.Label>Service Name</Form.Label>
              <Form.Control
                type="text"
                name="labServiceName"
                value={currentEditLabService?.labServiceName || ''}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group controlId="labServiceAmount">
              <Form.Label>Service Amount</Form.Label>
              <Form.Control
                type="text"
                name="labServiceAmount"
                value={currentEditLabService?.labServiceAmount || ''}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group controlId="labServiceDescription">
              <Form.Label>Service Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="labServiceDescription"
                value={currentEditLabService?.labServiceDescription || ''}
                onChange={handleInputChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleEditLabService}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LabServices;
